import { CardGiftcard } from "@mui/icons-material";
import { Box, Button, Divider, Sheet, Stack, Typography } from "@mui/joy";
import {
  ApiQuestCostEquivalent,
  ApiQuestCostEstimate,
  ApiQuestCostScenario,
  ApiQuestDetail,
} from "griptonite-api-client/types";
import { useState } from "react";

import { useVenueQuery } from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import QuestCostDetailsModal from "./QuestCostDetailsModal";
import useQuestRulesCostEstimate from "./useQuestRulesCostEstimate";

// The model's history months, as `MONTH_WEIGHTS` in the API sets them.
const HISTORY_MONTHS = 3;

/** The credits a points quest is expected to cost, from the Rules tab's form as it stands. */
export default function QuestRulesPreview({ quest }: { quest: ApiQuestDetail }) {
  const { data: estimate, isError, isFetching } = useQuestRulesCostEstimate(quest);
  const { data: venue } = useVenueQuery(quest.venue.uuid);

  // The quest starts at midnight on the 1st in the gym's timezone, which can be the previous
  // month in the browser's.
  const month = quest.startTime
    ? new Intl.DateTimeFormat(undefined, {
        month: "long",
        year: "numeric",
        timeZone: venue?.timezone,
      }).format(new Date(quest.startTime))
    : null;

  return (
    <Stack sx={{ backgroundColor: "white", borderRadius: 8, padding: 1.5 }} gap={1.5}>
      <Typography level="title-md">Cost modelling</Typography>
      {isError && !estimate ? (
        <Typography level="body-sm" color="danger">
          Couldn&apos;t estimate the cost. Please try again or contact support.
        </Typography>
      ) : !estimate ? (
        <Typography level="body-sm" color="neutral">
          Estimating…
        </Typography>
      ) : (
        // Dimmed while the estimate catches up with the latest edits.
        <Stack gap={1.5} sx={{ opacity: isFetching ? 0.5 : 1, transition: "opacity 0.2s" }}>
          <EstimateBody estimate={estimate} month={month} />
        </Stack>
      )}
    </Stack>
  );
}

const EstimateBody = ({
  estimate,
  month,
}: {
  estimate: ApiQuestCostEstimate;
  month: string | null;
}) => {
  const { low, central, high } = estimate;
  if (estimate.status === "INSUFFICIENT_DATA" || !low || !central || !high) {
    return (
      <Typography level="body-sm" color="neutral">
        There isn&apos;t enough climbing history at this gym to estimate the cost yet.
      </Typography>
    );
  }

  return (
    <>
      <Stack gap={1}>
        <Typography level="body-sm" color="neutral">
          {month ? `${month} estimate:` : "Estimate:"}
        </Typography>
        <Stack direction="row" gap={1}>
          <Stat value={central.participants} label="entries" />
          <Stat value={central.credits} label="credits earned" />
        </Stack>
      </Stack>
      {estimate.equivalents.length ? (
        <>
          <Stack gap={1}>
            <Typography level="body-sm" color="neutral">
              In terms of rewards:
            </Typography>
            {estimate.equivalents.map((equivalent) => (
              <Equivalent key={equivalent.product} equivalent={equivalent} />
            ))}
          </Stack>
        </>
      ) : null}
      <Divider />
      <Breakdown scenario={central} />
      <Typography level="body-xs" color="neutral">
        Based on the last {HISTORY_MONTHS} months of climbing data, where you&apos;ve had about{" "}
        {estimate.activeLoggers.toLocaleString()} people logging here per month.
      </Typography>
      <DetailsButton estimate={estimate} />
    </>
  );
};

// "130× Flat white", since reward names don't reliably pluralise.
const Equivalent = ({ equivalent }: { equivalent: ApiQuestCostEquivalent }) => (
  <Stack direction="row" gap={1.5} alignItems="center">
    <Box
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 6,
        overflow: "hidden",
        backgroundColor: "neutral.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "neutral.400",
      }}
    >
      {equivalent.image ? (
        <img
          alt=""
          src={getThumbnailUrl(equivalent.image.url)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <CardGiftcard />
      )}
    </Box>
    <Stack>
      <Typography level="title-lg">
        {equivalent.count.toLocaleString()}× {equivalent.name}
      </Typography>
      <Typography level="body-sm" color="neutral">
        ≈ <Typography fontWeight="lg">{equivalent.perParticipant.toLocaleString()}</Typography> per
        entry
      </Typography>
    </Stack>
  </Stack>
);

const Stat = ({ value, label }: { value: number; label: string }) => (
  <Sheet
    variant="outlined"
    sx={{
      flex: 1,
      borderRadius: 8,
      paddingX: 1.25,
      paddingY: 1,
      h3: {
        lineHeight: 1.1,
      },
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Typography level="h3">{value.toLocaleString()}</Typography>
    <Typography level="body-sm" color="neutral">
      {label}
    </Typography>
  </Sheet>
);

const DetailsButton = ({ estimate }: { estimate: ApiQuestCostEstimate }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" size="sm" onClick={() => setOpen(true)}>
        Details
      </Button>
      <QuestCostDetailsModal estimate={estimate} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

// In credits, since that's what each part of the quest pays out in.
const Breakdown = ({ scenario }: { scenario: ApiQuestCostScenario }) => (
  <Stack gap={0.5}>
    {[
      { label: "Milestones", credits: scenario.thresholdCredits },
      { label: "Side quests", credits: scenario.sideQuestCredits },
      { label: "Boosts", credits: scenario.boostCredits },
    ].map(({ label, credits }) => (
      <Stack key={label} direction="row" justifyContent="space-between">
        <Typography level="body-sm">{label}</Typography>
        <Typography level="body-sm">{credits.toLocaleString()}</Typography>
      </Stack>
    ))}
  </Stack>
);
